/**
 * DialogShell — universal modal wrapper.
 *
 * Pulls together every overlay concern (Esc, backdrop click, hardware-
 * back / browser-back close, focus trap, body scroll-lock, aria-modal,
 * aria-labelledby, dismissible-by-default) so individual modals only
 * have to provide content.
 *
 * Audit 2026-06-08 introduced this as the cure for ~32 hand-rolled
 * overlays that each had partial dismissal handling. New overlays
 * SHOULD use DialogShell; existing ones are being migrated.
 *
 * Variant prop:
 *   "center"      — classic centered modal (default).
 *   "right-drawer"— full-height slide-from-right panel (used in
 *                   admin Users / Orgs / Feedback drawers).
 *   "fullscreen"  — phone-takeover sheet (used by the mobile chat).
 *
 * Children render the BODY ONLY — DialogShell provides the chrome:
 *   <DialogShell open={open} onClose={close} title="Edit ball" labelledBy="...">
 *     <YourContent />
 *   </DialogShell>
 *
 * If you need a custom header (e.g. tabs, multi-step indicator),
 * pass `renderHeader`; the close button still gets injected at the
 * top-right unless `hideClose` is set.
 */
import { useRef } from "react";
import { X } from "lucide-react";
import useDialogDismiss from "../../hooks/useDialogDismiss";

const VARIANTS = {
  center: {
    backdrop: "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4",
    panel:   "relative bg-bg-primary text-text-primary border border-border-default rounded-lg shadow-2xl w-full",
  },
  "right-drawer": {
    backdrop: "fixed inset-0 z-50 bg-black/50 flex justify-end",
    panel:   "relative bg-bg-primary text-text-primary border-l border-border-default shadow-2xl h-full overflow-y-auto",
  },
  fullscreen: {
    backdrop: "fixed inset-0 z-50 bg-bg-primary flex flex-col",
    panel:   "relative flex-1 overflow-y-auto",
  },
};

export default function DialogShell({
  open,
  onClose,
  title,
  ariaLabel,
  labelledBy,
  variant = "center",
  size = "lg",         // sm | md | lg | xl | 2xl | 3xl | 4xl
  hideClose = false,
  closeOnBackdrop = true,
  renderHeader,
  className = "",
  // Lower-level escape hatches for unusual cases — match the host's
  // useDialogDismiss defaults.
  esc = true,
  historyBack = true,
  focusTrap = true,
  scrollLock = true,
  children,
}) {
  const panelRef = useRef(null);
  useDialogDismiss({ open, onClose, ref: panelRef, esc, historyBack, focusTrap, scrollLock });

  if (!open) return null;
  const v = VARIANTS[variant] || VARIANTS.center;

  const SIZE_CLASS = {
    sm:   "max-w-sm",
    md:   "max-w-md",
    lg:   "max-w-lg",
    xl:   "max-w-xl",
    "2xl":"max-w-2xl",
    "3xl":"max-w-3xl",
    "4xl":"max-w-4xl",
  };
  const panelClass = [
    v.panel,
    variant === "center" ? `${SIZE_CLASS[size] || SIZE_CLASS.lg} max-h-[90vh] overflow-hidden flex flex-col` : "",
    variant === "right-drawer" ? "w-full max-w-md" : "",
    className,
  ].filter(Boolean).join(" ");

  const onBackdropClick = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className={v.backdrop}
      onClick={onBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        {...(labelledBy ? { "aria-labelledby": labelledBy } : { "aria-label": ariaLabel || title || "Dialog" })}
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header strip — render the custom header (if provided) OR a
            default title bar with the X. Close button is injected
            unless hideClose is true. */}
        {renderHeader ? (
          <div className="relative">
            {renderHeader}
            {!hideClose && (
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-3 right-3 p-1 rounded text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        ) : title ? (
          <div className="px-5 py-3 border-b border-border-default flex items-center justify-between shrink-0">
            <h2 className="text-lg font-semibold">{title}</h2>
            {!hideClose && (
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-1 rounded text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        ) : !hideClose ? (
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 z-10 p-1 rounded text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        ) : null}

        {/* Body — children render here. For center variant we wrap
            with overflow-y-auto + flex-1 so long content scrolls
            inside the panel rather than the page. */}
        {variant === "center" ? (
          <div className="flex-1 overflow-y-auto">{children}</div>
        ) : children}
      </div>
    </div>
  );
}
